<template>
  <Dialog v-model="visible" :title="$t('monopoly.loginTitle')">
    <div class="login-dialog">
      <div class="form-item">
        <label>{{ $t('monopoly.accountId') }}</label>
        <input
          v-model="state.account"
          type="text"
          :placeholder="$t('monopoly.login.emailPlaceholder')"
        />
      </div>
      <div class="form-item">
        <label>{{ $t('monopoly.login.inputCode') }}</label>
        <div class="code-row">
          <input
            v-model="state.code"
            type="text"
            :placeholder="$t('monopoly.login.codePlaceholder')"
            maxlength="6"
          />
          <button
            class="send-code-btn"
            :disabled="state.countdown > 0"
            @click="handleSendCode"
          >
            {{
              state.countdown > 0
                ? state.countdown + ' s'
                : $t('monopoly.login.sendCode')
            }}
          </button>
        </div>
      </div>
      <div class="actions">
        <Btn type="yellow" @click="handleClose">
          {{ $t('monopoly.login.cancel') }}
        </Btn>
        <Btn type="blue" @click="handleLogin">
          {{ $t('monopoly.login.submit') }}
        </Btn>
      </div>
    </div>
  </Dialog>
</template>

<script setup>
import { computed, reactive, watch, onUnmounted, defineProps, defineEmits } from 'vue'
import { useI18n } from 'vue-i18n'
import { toastService } from '@/utils/toastService'
import MonopolyApi from '@/views/act/monopoly/api'
import { trackLogin, PAGE_SOURCE_OUT } from '@/views/act/monopoly/utils/track'
import Dialog from '../Dialog/index.vue'
import Btn from '../Btn/index.vue'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue', 'loginSuccess'])
const { t } = useI18n()

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const state = reactive({
  account: '',
  code: '',
  countdown: 0,
  timer: null,
})

const handleClose = () => {
  visible.value = false
}

const startCountdown = () => {
  state.countdown = 60
  state.timer = setInterval(() => {
    state.countdown--
    if (state.countdown <= 0) {
      clearInterval(state.timer)
      state.timer = null
      state.countdown = 0
    }
  }, 1000)
}

const handleSendCode = async () => {
  if (!state.account) {
    toastService.show(t('monopoly.login.emailPlaceholder'))
    return
  }
  if (state.countdown > 0) return
  try {
    await MonopolyApi.sendMailCode({ uid: state.account.toUpperCase() })
    startCountdown()
    toastService.show(t('monopoly.login.codeSent'))
  } catch (e) {
    console.error(e)
  }
}

const handleLogin = async () => {
  if (!state.account) {
    toastService.show(t('monopoly.login.emailPlaceholder'))
    return
  }
  if (!state.code) {
    toastService.show(t('monopoly.login.codePlaceholder'))
    return
  }
  try {
    const res = await MonopolyApi.loginByMailCode({
      uid: state.account.toUpperCase(),
      code: state.code,
    })
    if (res && res.authorization) {
      trackLogin(PAGE_SOURCE_OUT)
      emit('loginSuccess')
      visible.value = false
      toastService.show(t('monopoly.login.loginSuccess'))
    }
  } catch (e) {
    console.error(e)
    toastService.show(t('monopoly.login.codeError'))
  }
}

watch(
  () => props.modelValue,
  (val) => {
    if (!val) {
      state.account = ''
      state.code = ''
      if (state.timer) {
        clearInterval(state.timer)
        state.timer = null
        state.countdown = 0
      }
    }
  }
)

onUnmounted(() => {
  if (state.timer) {
    clearInterval(state.timer)
  }
})
</script>

<style lang="less" scoped>
.login-dialog {
  width: 100%;
  box-sizing: border-box;
  color: #12215c;
}

.form-item {
  width: 100%;
  margin-bottom: 20px;

  label {
    display: block;
    font-size: 22px;
    margin-bottom: 8px;
  }

  input {
    width: 100%;
    height: 48px;
    padding: 0 12px;
    border: 1px solid #c5cbe0;
    border-radius: 8px;
    font-size: 22px;
    box-sizing: border-box;
  }
}

.code-row {
  display: flex;
  gap: 12px;

  input {
    flex: 1;
  }

  .send-code-btn {
    flex-shrink: 0;
    min-width: 160px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    background: #23B0FB;
    color: #fff;
    font-size: 18px;
    cursor: pointer;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.actions {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 28px;
}
</style>
